import React from "react";
import _ from "lodash";
import { useDispatch } from "react-redux";
import {TRANSACTION_TYPE_VALUES} from "../../../../../constants";
import {transactionValueToEuro} from "../../../../../utils";
import {
  PORTFOLIO_BUILDER_NO_ASSETS_MSG,
} from '../../../../../../RiskProfiling/utils';
import {useConfirmationModalContext} from "../../../../../../../components/ConfirmationModalContextProvider";
import {displayWarningSnackBar} from '../../../../../../../components/SnackbarProvider/actions';
import GoogleAnalytics from "../../../../../../../utils/GoogleAnalytics";
import {
  createInstrumentHandlers,
  createSwitchInHandler,
  getPortfolioInstruments,
  createHandleAddItems,
  prepareRecommendationsForTransactions,
  addRecommendationsToTransactions,
} from "../../tradingOperations";
import {
  computeTransactionValue,
  computeSwitchAmountEuro,
} from "../../transactionCalculations";


/**
 * Self-contained hook for the Portfolio Builder feature.
 * Creates its own instrument handlers and transaction operations.
 */
const usePortfolioBuilder = ({portfolio, dataService, isVirtual, handlePortfolioChange, allowedTradeOptions, initialActive = false}) => {
  const dispatch = useDispatch();
  const [active, setActive] = React.useState(initialActive);
  const modalContext = useConfirmationModalContext();

  const open = () => {
    setActive(true);
    GoogleAnalytics.trackPortfolioBuilderOpen();
  };

  const close = () => {
    setActive(false);
  };

  const setCaseData = (data) => {
    handlePortfolioChange(portfolio, {portfolioBuilderData: data});
  };

  const setRecommendations = async (assets) => {
    const transactions = portfolio.transactions;

    if (!_.isEmpty(_.flatten(Object.values(transactions))) && !await modalContext.confirm("Alle vorherigen Ordereingaben werden gelöscht. Sind Sie sicher?")) {
      setActive(false);
      return;
    }

    // Clear existing
    transactions[TRANSACTION_TYPE_VALUES.BUY] = [];
    transactions[TRANSACTION_TYPE_VALUES.SELL] = [];
    transactions[TRANSACTION_TYPE_VALUES.SWITCH] = [];

    const handlers = createInstrumentHandlers(portfolio, dataService, isVirtual);
    const switchInHandler = createSwitchInHandler(portfolio, dataService, isVirtual);

    let buy = [], buyExcluded;
    let sell = [], sellExcluded;
    const currentAssetsForBuy = getPortfolioInstruments(portfolio, transactions, isVirtual, TRANSACTION_TYPE_VALUES.BUY);
    const currentAssetsForSell = getPortfolioInstruments(portfolio, transactions, isVirtual, TRANSACTION_TYPE_VALUES.SELL);

    assets.forEach(assetInfo => {
      switch (assetInfo.recommendationType) {
        case 'BUY':
          const currentAsset = _.find(currentAssetsForBuy, i => i.isin === assetInfo.isin);
          buy.push({...currentAsset, ...assetInfo, newMarketValue: assetInfo.amount});
          break;
        case 'SELL':
          let qtyRemains = assetInfo.qty, qtyToSell;
          currentAssetsForSell.forEach(i => {
            if (i.isin === assetInfo.isin && qtyRemains > 0) {
              qtyToSell = _.min([i.quantity, qtyRemains]);
              sell.push({
                ...i, ...assetInfo,
                newMarketValue: _.round(transactionValueToEuro(qtyToSell, {data: i}, false), 2),
                newQty: qtyToSell,
              });
              qtyRemains = _.floor(qtyRemains - qtyToSell, portfolio.maxQtyDecimals || 3);
            }
          });
          break;
        case 'COMPLETE_SELL':
          currentAssetsForSell.forEach(i => {
            if (i.isin === assetInfo.isin) {
              sell.push({...i, ...assetInfo, newMarketValue: i.market_value, newQty: i.quantity});
            }
          });
          break;
      }
    });

    // Prepare switches
    let switches;
    [buy, sell, switches] = await prepareRecommendationsForTransactions({
      buy, sell, portfolio, allowedTradeOptions, switchOutHandler: handlers[TRANSACTION_TYPE_VALUES.SWITCH],
      switchInHandler,
    });

    // Filter with custodian
    const buyHandler = handlers[TRANSACTION_TYPE_VALUES.BUY];
    const sellHandler = handlers[TRANSACTION_TYPE_VALUES.SELL];
    if (buyHandler) {
      [buy, buyExcluded] = await buyHandler.getCustodianAndFilterInstruments(buy, true);
      buy = buy.concat(buyExcluded);
    }
    if (sellHandler) {
      [sell, sellExcluded] = await sellHandler.getCustodianAndFilterInstruments(sell, true);
      sell = sell.concat(sellExcluded);
    }

    if (_.isEmpty(buy) && _.isEmpty(sell) && _.isEmpty(switches)) {
      dispatch(displayWarningSnackBar(PORTFOLIO_BUILDER_NO_ASSETS_MSG));
    } else {
      const handleTransactionValueChange = (item, value, switchOutItem, withEuro=true) => {
        const result = computeTransactionValue(value, item, switchOutItem, withEuro, portfolio.maxQtyDecimals);
        Object.assign(item, result);
      };

      const handleSwitchAmountEuroChanged = (item, value, switchOutItem) => {
        const result = computeSwitchAmountEuro(value, item, switchOutItem, portfolio.maxQtyDecimals);
        Object.assign(item, result);
      };

      const handleAddItems = createHandleAddItems({
        portfolio, transactions, isVirtual,
        isPassiveDepot: _.get(portfolio, 'data.is_passive'),
        handleTransactionValueChange,
        handleSwitchAmountEuroChanged,
        handlePortfolioChange, dispatch, modalContext,
      });

      await addRecommendationsToTransactions({
        buy, sell, switches, handleAddItems, transactions,
      });
    }

    setActive(false);
    handlePortfolioChange(portfolio, {transactions: {...transactions}});
    GoogleAnalytics.trackPortfolioBuilderResults();
  };

  return {
    active,
    open,
    close,
    caseData: portfolio ? (portfolio.portfolioBuilderData || {}) : {},
    setCaseData,
    setRecommendations,
  };
};

export default usePortfolioBuilder;
